import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { format, isSameDay, parseISO } from "date-fns";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { useNavigate } from "react-router-dom";
import { useSearch } from "../contexts/SearchContext";
import { useApi } from "../hooks/useApi";
import DetailsTable from "./DetailsTable";

interface ReportData {
  [key: string]: any;
}

interface ReportsTableProps {
  searchTerm?: string;
  highlightTerm?: string;
  hideSearch?: boolean;
}

type SortDirection = "asc" | "desc" | null;

interface SortState {
  column: string | null;
  direction: SortDirection;
}

const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const tokenizeSearch = (value: string): string[] => {
  const trimmed = (value || "").trim().toLowerCase();
  if (!trimmed) return [];
  const tokens: string[] = [];
  const regex = /\"([^\"]+)\"|([^\s]+)/g; // "phrase" or singleWord
  let match: RegExpExecArray | null;
  while ((match = regex.exec(trimmed)) !== null) {
    const phrase = (match[1] || match[2] || "").trim();
    if (phrase.length > 0) tokens.push(phrase);
  }
  return tokens;
};

const HighlightText = ({
  text,
  searchTerm,
}: {
  text: string | number;
  searchTerm: string;
}) => {
  if (!text) return <>{text}</>;
  const tokens = tokenizeSearch(searchTerm);
  if (tokens.length === 0) return <>{text}</>;

  const pattern = new RegExp(`(${tokens.map(escapeRegex).join("|")})`, "gi");
  const parts = String(text).split(pattern);
  const tokenSet = new Set(tokens.map((t) => t.toLowerCase()));

  return (
    <>
      {parts.map((part, i) => {
        const lower = part.toLowerCase();
        if (tokenSet.has(lower)) {
          return (
            <span
              key={i}
              style={{
                backgroundColor: "#ff9800",
                color: "#000",
                padding: "0 2px",
                borderRadius: "2px",
                fontWeight: "bold",
              }}
            >
              {part}
            </span>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
};

const ReportsTable: React.FC<ReportsTableProps> = ({
  searchTerm: externalSearchTerm,
  highlightTerm,
  hideSearch = false,
}) => {
  const {
    reports,
    loading,
    error,
    counts,
    fetchData,
    searchTerm,
    setSearchTerm,
    pendingSearchTerm,
    setPendingSearchTerm,
    selectedDate,
    setSelectedDate,
    triggerSearch,
    clearSearch,
    availableLocations,
    selectedLocation,
    setSelectedLocation,
    locationLoading,
  } = useSearch();

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(100);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const { callApi } = useApi();
  const navigate = useNavigate();
  const [sortState, setSortState] = useState<SortState>({
    column: null,
    direction: null,
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [assessingRigs, setAssessingRigs] = useState<Set<string>>(new Set());
  const [rigAssessments, setRigAssessments] = useState<
    Record<string, { data?: any; loading?: boolean; error?: string }>
  >({});
  const [dayPickerMonth, setDayPickerMonth] = useState<Date>(selectedDate);

  const safeFormatDate = (val?: string): string => {
    try {
      if (!val) return "";
      const d = parseISO(val);
      if (isNaN(d.getTime())) return "";
      return format(d, "yyyy-MM-dd");
    } catch {
      return "";
    }
  };

  // Define the column configuration
  const defaultColumns = [
    { name: "Date", width: "110px" },
    { name: "Rig", width: "100px" },
    { name: "Well", width: "150px" },
    { name: "Last24", flex: 1 },
    { name: "Next24", flex: 1 },
    { name: "Plugging", width: "110px" },
    { name: "Phase", width: "110px" },
    { name: "Manager", width: "220px" },
    { name: "FileUrl", width: "210px" },
  ];

  const omanColumns = [
    { name: "Date", width: "110px" },
    { name: "Rig", width: "100px" },
    { name: "Well", width: "150px" },
    { name: "Hole", width: "100px" },
    { name: "Contact", width: "150px" },
    { name: "Summary", flex: 1 },
    { name: "FileUrl", width: "210px" },
  ];

  // Abu Dhabi columns - no Plugging, Phase, Manager
  const abuDhabiColumns = [
    { name: "Date", width: "110px" },
    { name: "Rig", width: "100px" },
    { name: "Well", width: "150px" },
    { name: "Last24", flex: 1 },
    { name: "Next24", flex: 1 },
    { name: "FileUrl", width: "210px" },
  ];

  // Get all unique column names from the reports, excluding Details, LostTimeDetails, and Id
  const availableColumns = Array.from(
    new Set(reports.flatMap((report) => Object.keys(report)))
  ).filter(
    (col) =>
      ![
        "Details",
        "LostTimeDetails",
        "Id",
        "CreatedAt",
        "SectionIndex",
        "CuId",
        "Content",
        "Location",
      ].includes(col)
  );

  // Filter columns based on availability and location
  const visibleColumns = useMemo(() => {
    if (selectedLocation === "Oman") {
      // Build dynamic Col1..ColN if present; otherwise fall back to legacy omanColumns
      const colNames = availableColumns
        .filter((n) => /^col\d+$/i.test(n))
        .sort(
          (a, b) =>
            Number(a.replace(/^col/i, "")) - Number(b.replace(/^col/i, ""))
        );

      if (colNames.length > 0) {
        const cols: { name: string; width?: string; flex?: number }[] = [];
        if (availableColumns.includes("Date"))
          cols.push({ name: "Date", width: "110px" });
        // Include derived fields if present
        if (availableColumns.includes("Rig"))
          cols.push({ name: "Rig", width: "100px" });
        if (availableColumns.includes("Well"))
          cols.push({ name: "Well", width: "150px" });
        if (availableColumns.includes("Hole"))
          cols.push({ name: "Hole", width: "100px" });
        for (const n of colNames) cols.push({ name: n, flex: 1 });
        if (availableColumns.includes("FileUrl"))
          cols.push({ name: "FileUrl", width: "210px" });
        return cols;
      }

      // Fallback to predefined Oman set if no generic columns yet
      return omanColumns.filter((col) => availableColumns.includes(col.name));
    }

    // Abu Dhabi - use simplified columns without Plugging, Phase, Manager
    if (selectedLocation === "AbuDhabi") {
      return abuDhabiColumns.filter((col) => availableColumns.includes(col.name));
    }

    return defaultColumns.filter(
      (col) =>
        col.name === "Plugging" ||
        col.name === "Phase" ||
        availableColumns.includes(col.name)
    );
  }, [availableColumns, omanColumns, abuDhabiColumns, defaultColumns, selectedLocation]);

  // Update active search term when search term changes
  useEffect(() => {
    if (searchTerm) {
      setActiveSearchTerm(searchTerm);
    } else {
      setActiveSearchTerm("");
    }
  }, [searchTerm]);

  const handleDateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDayPickerMonth(selectedDate);
    setAnchorEl(event.currentTarget);
  };

  const handleDateClose = () => {
    setAnchorEl(null);
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date || new Date());
    handleDateClose();
    // Date changes are automatically detected by SearchContext
  };

  const open = Boolean(anchorEl);

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle loading state
  useEffect(() => {
    if (loading) {
      setTableLoading(true);
    } else {
      const timer = setTimeout(() => {
        setTableLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // Get current page data with sorting
  const { sortedReports, currentPageData } = useMemo(() => {
    let sorted = [...reports];

    // Apply sorting if a column is selected
    if (sortState.column && sortState.direction) {
      sorted.sort((a, b) => {
        if (sortState.column === "Plugging") {
          const score = (rig: string) => {
            const aData = rigAssessments[rig]?.data;
            const raw = (
              aData?.possiblePlugging ??
              aData?.PossiblePlugging ??
              ""
            )
              .toString()
              .trim()
              .toLowerCase();
            if (["yes", "true", "y", "1"].includes(raw)) return 2;
            if (["no", "false", "n", "0"].includes(raw)) return 1;
            return 0;
          };
          const sa = score(a.Rig || "");
          const sb = score(b.Rig || "");
          return sortState.direction === "asc" ? sa - sb : sb - sa;
        }

        if (sortState.column === "Phase") {
          const val = (rig: any) => {
            const data = rigAssessments[rig.Rig]?.data;
            return (data?.phase ?? data?.Phase ?? "").toString().toLowerCase();
          };
          const va = val(a);
          const vb = val(b);
          return sortState.direction === "asc"
            ? va.localeCompare(vb)
            : vb.localeCompare(va);
        }

        const aValue = a[sortState.column!]?.toString().toLowerCase() || "";
        const bValue = b[sortState.column!]?.toString().toLowerCase() || "";
        if (sortState.direction === "asc") {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      });
    }

    const startIndex = page * rowsPerPage;
    const pageData = sorted.slice(startIndex, startIndex + rowsPerPage);

    return { sortedReports: sorted, currentPageData: pageData };
    // NOTE: rigAssessments influences sorting for Plugging & Phase, so include it
  }, [reports, page, rowsPerPage, sortState, rigAssessments]);

  const handleRigClick = async (rigName: string) => {
    try {
      setSearchTerm(rigName);
      setActiveSearchTerm(rigName);
      // Search term changes are automatically detected by SearchContext
    } catch (err) {
      console.error("Rig reports fetch error:", err);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value || "";
    setPendingSearchTerm(value);
  };

  const handleSearchKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      console.log("🔍 Enter pressed, searching for:", pendingSearchTerm);
      setSearchTerm(pendingSearchTerm);
      setActiveSearchTerm(pendingSearchTerm);
      // Search term changes are automatically detected by SearchContext
    }
  };

  const handleClearSearch = () => {
    setActiveSearchTerm("");
    clearSearch();
  };

  const handleResetSearch = () => {
    setActiveSearchTerm("");
    clearSearch();
  };

  const handleRowExpand = (reportId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };

  const handleShowAll = (rig: string) => {
    navigate(`/rig/${encodeURIComponent(rig)}`);
  };

  const handleAssessRig = async (rigName: string) => {
    try {
      setAssessingRigs((prev) => new Set(prev).add(rigName));
      const body = {
        RigName: rigName,
        Location: selectedLocation || undefined,
        HistoryDays: 60,
      };
      const response = await callApi(`/api/RigAssessments/run`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.text();
        throw new Error(err || "Rig assessment failed");
      }
      // Optionally handle returned assessment
      const result = await response.json();
      console.log("Rig assessment result:", result);
      setRigAssessments((prev) => ({
        ...prev,
        [rigName]: { data: result, loading: false },
      }));
    } catch (err) {
      console.error("Rig assessment error:", err);
    } finally {
      setAssessingRigs((prev) => {
        const next = new Set(prev);
        next.delete(rigName);
        return next;
      });
    }
  };

  const fetchLatestAssessment = useCallback(
    async (rigName: string) => {
      setRigAssessments((prev) => {
        if (prev[rigName]?.data || prev[rigName]?.loading) return prev;
        return { ...prev, [rigName]: { loading: true } };
      });
      try {
        const locParam = selectedLocation
          ? `&location=${encodeURIComponent(selectedLocation)}`
          : "";
        const response = await callApi(
          `/api/RigAssessments/latest?rigName=${encodeURIComponent(
            rigName
          )}${locParam}`
        );
        if (response.status === 204) {
          setRigAssessments((prev) => ({
            ...prev,
            [rigName]: { data: undefined, loading: false },
          }));
          return;
        }
        const data = await response.json();
        setRigAssessments((prev) => ({
          ...prev,
          [rigName]: { data, loading: false },
        }));
      } catch (e: any) {
        setRigAssessments((prev) => ({
          ...prev,
          [rigName]: {
            error: e?.message || "Failed to load assessment",
            loading: false,
          },
        }));
      }
    },
    [callApi, selectedLocation]
  );

  // Batch fetch latest assessments for rigs on current page to avoid N requests
  useEffect(() => {
    const rigNames = Array.from(
      new Set(currentPageData.map((r) => r.Rig).filter(Boolean))
    );
    if (rigNames.length === 0) return;
    const missing = rigNames.filter((r) => !rigAssessments[r]);
    if (missing.length === 0) return;
    (async () => {
      try {
        const payload = {
          RigNames: missing,
          Location: selectedLocation || undefined,
        } as any;
        const resp = await callApi("/api/RigAssessments/latest-many", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        if (!resp.ok) return;
        const data = await resp.json();
        setRigAssessments((prev) => {
          const next = { ...prev } as any;
          Object.keys(data || {}).forEach((rig: string) => {
            next[rig] = { data: data[rig], loading: false };
          });
          // Ensure we set empty entries for rigs without data
          missing.forEach((rig) => {
            if (!next[rig]) next[rig] = { data: undefined, loading: false };
          });
          return next;
        });
      } catch {
        /* ignore */
      }
    })();
  }, [currentPageData, selectedLocation]);

  const resolvePluggingColor = (rigName: string): string => {
    const a = rigAssessments[rigName]?.data;
    if (!a) return "white";
    const raw = (a.possiblePlugging ?? a.PossiblePlugging ?? "")
      .toString()
      .trim()
      .toLowerCase();
    if (["yes", "true", "y", "1"].includes(raw)) return "success.main";
    if (["no", "false", "n", "0"].includes(raw)) return "error.main";
    return "white";
  };

  const handleSort = (column: string) => {
    setSortState((prev) => {
      if (prev.column === column) {
        // Cycle through: asc → desc → null
        if (prev.direction === "asc") {
          return { column, direction: "desc" };
        } else if (prev.direction === "desc") {
          return { column: null, direction: null };
        } else {
          return { column, direction: "asc" };
        }
      } else {
        // New column, start with asc
        return { column, direction: "asc" };
      }
    });
  };

  // Determine if there are any missing predictions (possiblePlugging) for rigs on the current page.
  // Avoid initial flicker: only evaluate/show button AFTER we have an assessment entry (data or empty) for every rig on the page.
  const { hasMissingPredictions, predictionsEvaluated } = useMemo(() => {
    if (selectedLocation === "Oman")
      return { hasMissingPredictions: false, predictionsEvaluated: false };
    const rigsOnPage = Array.from(
      new Set(currentPageData.map((r) => r.Rig).filter(Boolean))
    );
    if (rigsOnPage.length === 0)
      return { hasMissingPredictions: false, predictionsEvaluated: false };

    // We consider predictions "evaluated" when each rig has an entry in rigAssessments (even if data undefined)
    const predictionsEvaluated = rigsOnPage.every((rig) =>
      rigAssessments.hasOwnProperty(rig)
    );
    if (!predictionsEvaluated)
      return { hasMissingPredictions: false, predictionsEvaluated: false };

    const hasMissingPredictions = rigsOnPage.some((rig) => {
      const assessment = rigAssessments[rig];
      if (!assessment || !assessment.data) return true; // no data present
      const val = (
        assessment.data.possiblePlugging ??
        assessment.data.PossiblePlugging ??
        ""
      )
        .toString()
        .trim();
      return val.length === 0;
    });
    return { hasMissingPredictions, predictionsEvaluated: true };
  }, [currentPageData, rigAssessments, selectedLocation]);

  // Reset page when sorting changes
  useEffect(() => {
    setPage(0);
  }, [sortState]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ mb: 2 }}>
        {!hideSearch && (
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Box sx={{ pt: 1 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by multiple words (use quotes for phrases)"
                    value={pendingSearchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyPress}
                    inputRef={searchInputRef}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {loading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSearchTerm(pendingSearchTerm);
                                setActiveSearchTerm(pendingSearchTerm);
                                // Search term changes are automatically detected by SearchContext
                              }}
                            >
                              <SearchIcon />
                            </IconButton>
                          )}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: activeSearchTerm
                          ? "rgba(255, 152, 0, 0.1)"
                          : "inherit",
                        "&:hover": {
                          backgroundColor: activeSearchTerm
                            ? "rgba(255, 152, 0, 0.15)"
                            : "inherit",
                        },
                        height: "56px",
                      },
                    }}
                  />
                  {activeSearchTerm && (
                    <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleClearSearch}
                        sx={{
                          color: "text.secondary",
                          "&:hover": {
                            color: "primary.main",
                          },
                        }}
                      >
                        Clear search
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", gap: 2, pt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleDateClick}
                    startIcon={<CalendarMonthIcon />}
                    sx={{
                      flex: 1,
                      height: "56px",
                      backgroundColor: !isSameDay(selectedDate, new Date())
                        ? "rgba(255, 152, 0, 0.1)"
                        : "inherit",
                      "&:hover": {
                        backgroundColor: !isSameDay(selectedDate, new Date())
                          ? "rgba(255, 152, 0, 0.15)"
                          : "inherit",
                      },
                    }}
                  >
                    {format(selectedDate, "yyyy-MM-dd")}
                  </Button>
                  {availableLocations.length > 1 && (
                    <FormControl sx={{ flex: 1, minWidth: 120 }}>
                      <InputLabel id="location-select-label">
                        Location
                      </InputLabel>
                      <Select
                        labelId="location-select-label"
                        value={selectedLocation || ""}
                        label="Location"
                        onChange={(event) =>
                          setSelectedLocation(event.target.value || null)
                        }
                        startAdornment={<LocationOnIcon sx={{ mr: 1 }} />}
                        sx={{
                          height: "56px",
                          backgroundColor: selectedLocation
                            ? "rgba(255, 152, 0, 0.1)"
                            : "inherit",
                          "&:hover": {
                            backgroundColor: selectedLocation
                              ? "rgba(255, 152, 0, 0.15)"
                              : "inherit",
                          },
                        }}
                        disabled={locationLoading}
                      >
                        {availableLocations.map((location) => (
                          <MenuItem key={location} value={location}>
                            {location}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                  {!isSameDay(selectedDate, new Date()) && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setSelectedDate(new Date());
                        // Date changes are automatically detected by SearchContext
                      }}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          color: "primary.main",
                        },
                      }}
                    >
                      Clear date
                    </Button>
                  )}
                  {availableLocations.length > 1 && selectedLocation && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setSelectedLocation(availableLocations[0])}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          color: "primary.main",
                        },
                      }}
                    >
                      Reset location
                    </Button>
                  )}
                </Box>
                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleDateClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                >
                  <DayPicker
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    month={dayPickerMonth}
                    onMonthChange={setDayPickerMonth}
                    styles={{
                      caption: { color: "white" },
                      day_selected: {
                        backgroundColor: "white",
                        color: "black",
                      },
                      day_today: { color: "#4caf50" },
                      day: { margin: 0 },
                      nav_button: { color: "white" },
                      nav_button_previous: { color: "white" },
                      nav_button_next: { color: "white" },
                    }}
                  />
                </Popover>
              </Grid>
            </Grid>
          </Box>
        )}
        <Box
          sx={{
            mt: 2,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {selectedLocation && (
            <Typography variant="body2" color="text.secondary">
              Location: {selectedLocation}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Reports: {counts.filteredReports} of {counts.totalReports}
          </Typography>

          {selectedLocation !== "Oman" && (
            <>
              <Typography variant="body2" color="text.secondary">
                Details: {counts.filteredDetails} of {counts.totalDetails}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Lost Time Details: {counts.filteredLostTimeDetails} of{" "}
                {counts.totalLostTimeDetails}
              </Typography>
            </>
          )}
          {predictionsEvaluated && hasMissingPredictions && (
            <Button
              variant="contained"
              startIcon={<LightbulbOutlinedIcon sx={{ color: "#ffeb3b" }} />}
              size="small"
              onClick={async () => {
                try {
                  const payload = {
                    Date: format(selectedDate, "yyyy-MM-dd"),
                    Location: selectedLocation || undefined,
                  };
                  const resp = await callApi(
                    "/api/RigAssessments/run-missing-for-date",
                    {
                      method: "POST",
                      body: JSON.stringify(payload),
                    }
                  );
                  if (!resp.ok) {
                    const err = await resp.text();
                    throw new Error(err || "Failed to queue predictions");
                  }
                } catch (e) {
                  console.error("Prediction queue error:", e);
                }
              }}
              sx={{
                ml: "auto", // push button to the right
                textTransform: "none",
                backgroundColor: "transparent",
                color: "white",
                border: "1px solid white",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
            >
              Run predictions for day
            </Button>
          )}
        </Box>
      </Box>
      <Paper
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Confirm delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will permanently delete all reports for{" "}
              {format(selectedDate, "yyyy-MM-dd")} in location "
              {selectedLocation || "N/A"}". This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              color="error"
              onClick={async () => {
                try {
                  setDeleting(true);
                  const formattedDate = format(selectedDate, "yyyy-MM-dd");
                  const response = await callApi(
                    `/api/Reports/by-date?reportDate=${encodeURIComponent(
                      formattedDate
                    )}&location=${encodeURIComponent(selectedLocation || "")}`,
                    { method: "DELETE" }
                  );
                  if (!response.ok) {
                    const err = await response.text();
                    throw new Error(err || "Delete failed");
                  }
                  setConfirmOpen(false);
                  await fetchData();
                } catch (e) {
                  console.error(e);
                } finally {
                  setDeleting(false);
                }
              }}
              disabled={!selectedLocation || deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            <TableContainer
              sx={{
                flex: 1,
                overflow: "auto",
                position: "relative",
                maxWidth: "100%",
              }}
            >
              {tableLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
              <Table
                stickyHeader
                size="small"
                sx={{ tableLayout: "fixed", width: "100%" }}
              >
                <TableHead>
                  <TableRow>
                    {selectedLocation !== "Oman" && (
                      <TableCell padding="checkbox" />
                    )}
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.name}
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: "bold",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                          width: column.width,
                          cursor:
                            column.name === "Rig" ||
                            column.name === "Well" ||
                            column.name === "Plugging" ||
                            column.name === "Phase"
                              ? "pointer"
                              : "default",
                          userSelect: "none",
                          "&:hover":
                            column.name === "Rig" ||
                            column.name === "Well" ||
                            column.name === "Plugging" ||
                            column.name === "Phase"
                              ? {
                                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                                }
                              : {},
                        }}
                        onClick={() => {
                          if (
                            column.name === "Rig" ||
                            column.name === "Well" ||
                            column.name === "Plugging" ||
                            column.name === "Phase"
                          ) {
                            handleSort(column.name);
                          }
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {(() => {
                            if (column.name === "FileUrl") return "Actions";
                            if (column.name === "Date") return "ReportDate";
                            return column.name;
                          })()}
                          {(column.name === "Rig" ||
                            column.name === "Well" ||
                            column.name === "Phase" ||
                            column.name === "Plugging") && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                minWidth: "20px",
                              }}
                            >
                              {sortState.column === column.name &&
                                sortState.direction === "asc" && (
                                  <ArrowUpwardIcon
                                    sx={{ fontSize: "16px", color: "#1976d2" }}
                                  />
                                )}
                              {sortState.column === column.name &&
                                sortState.direction === "desc" && (
                                  <ArrowDownwardIcon
                                    sx={{ fontSize: "16px", color: "#1976d2" }}
                                  />
                                )}
                              {(sortState.column !== column.name ||
                                sortState.direction === null) && (
                                <Box
                                  sx={{
                                    fontSize: "14px",
                                    opacity: 0.3,
                                    fontWeight: "normal",
                                  }}
                                >
                                  ↕
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentPageData.map((report, index) => (
                    <React.Fragment key={index}>
                      <TableRow hover>
                        {selectedLocation !== "Oman" && (
                          <TableCell padding="checkbox">
                            <IconButton
                              size="small"
                              onClick={() => handleRowExpand(report.Id)}
                              sx={{
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                                },
                              }}
                            >
                              {expandedRows.has(report.Id) ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          </TableCell>
                        )}
                        {visibleColumns.map((column) => (
                          <TableCell
                            key={`${index}-${column.name}`}
                            sx={{
                              fontSize: "0.75rem",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                              width: column.width,
                            }}
                          >
                            {column.name === "FileUrl" ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  flexWrap: "wrap",
                                  maxWidth: "100%",
                                }}
                              >
                                {report[column.name] ? (
                                  <>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      href={report[column.name]}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        fontSize: "0.75rem",
                                        textTransform: "none",
                                        backgroundColor: "transparent",
                                        color: "white",
                                        "& .MuiSvgIcon-root": {
                                          color: "white",
                                        },
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(255, 255, 255, 0.1)",
                                        },
                                      }}
                                    >
                                      Open Report
                                    </Button>
                                    {report.ImageUrl && selectedLocation !== "AbuDhabi" && (
                                      <Button
                                        variant="contained"
                                        size="small"
                                        href={report.ImageUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                          fontSize: "0.75rem",
                                          textTransform: "none",
                                          backgroundColor: "transparent",
                                          color: "white",
                                          "& .MuiSvgIcon-root": {
                                            color: "white",
                                          },
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(255, 255, 255, 0.1)",
                                          },
                                        }}
                                      >
                                        Show Image
                                      </Button>
                                    )}
                                    {selectedLocation !== "Oman" && selectedLocation !== "AbuDhabi" && (
                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() =>
                                          handleShowAll(report.Rig)
                                        }
                                        sx={{
                                          fontSize: "0.75rem",
                                          textTransform: "none",
                                          backgroundColor: "transparent",
                                          color: "white",
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(255, 255, 255, 0.1)",
                                          },
                                        }}
                                      >
                                        Show History
                                      </Button>
                                    )}
                                    {selectedLocation !== "Oman" && selectedLocation !== "AbuDhabi" && (
                                      <Tooltip
                                        title={(() => {
                                          const state =
                                            rigAssessments[report.Rig];
                                          if (state?.loading)
                                            return "Loading assessment...";
                                          if (state?.error) return state.error;
                                          const a = state?.data;
                                          if (!a) return "No assessment yet";
                                          return (
                                            <Box sx={{ p: 1 }}>
                                              <Typography variant="caption">
                                                Phase:{" "}
                                                {a.phase || a.Phase || "-"}
                                              </Typography>
                                              <br />
                                              <Typography variant="caption">
                                                PossiblePlugging:{" "}
                                                {a.possiblePlugging ||
                                                  a.PossiblePlugging ||
                                                  "-"}
                                              </Typography>
                                              <br />
                                              <Typography variant="caption">
                                                Reason:{" "}
                                                {a.reason || a.Reason || "-"}
                                              </Typography>
                                              <br />
                                              <Typography variant="caption">
                                                ReasonPlugging:{" "}
                                                {a.reasonPlugging ||
                                                  a.ReasonPlugging ||
                                                  "-"}
                                              </Typography>
                                              <br />
                                              <Typography variant="caption">
                                                Run:{" "}
                                                {a.runDate
                                                  ? new Date(
                                                      a.runDate
                                                    ).toLocaleString()
                                                  : "-"}
                                              </Typography>
                                            </Box>
                                          );
                                        })()}
                                        arrow
                                      >
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={async () =>
                                              await handleAssessRig(report.Rig)
                                            }
                                            onMouseEnter={() =>
                                              fetchLatestAssessment(report.Rig)
                                            }
                                            disabled={assessingRigs.has(
                                              report.Rig
                                            )}
                                            sx={{
                                              color: resolvePluggingColor(
                                                report.Rig
                                              ),
                                              "&:hover": {
                                                backgroundColor:
                                                  "rgba(255, 255, 255, 0.1)",
                                              },
                                            }}
                                          >
                                            <AssessmentIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    )}
                                    {selectedLocation !== "Oman" && (
                                      <Tooltip title="Rerun Report">
                                        <IconButton
                                          size="small"
                                          onClick={async () => {
                                            try {
                                              const response = await callApi(
                                                `/api/Reports/${report.Id}/rerun`,
                                                {
                                                  method: "POST",
                                                }
                                              );
                                              if (response.ok) {
                                                // Refresh the table data
                                                fetchData();
                                              } else {
                                                const error =
                                                  await response.json();
                                                console.error(
                                                  "Rerun report error:",
                                                  error
                                                );
                                              }
                                            } catch (err) {
                                              console.error(
                                                "Rerun report error:",
                                                err
                                              );
                                            }
                                          }}
                                          sx={{
                                            color: "white",
                                            "&:hover": {
                                              backgroundColor:
                                                "rgba(255, 255, 255, 0.1)",
                                            },
                                          }}
                                        >
                                          <RefreshIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </>
                                ) : null}
                              </Box>
                            ) : column.name === "Rig" ? (
                              <HighlightText
                                text={report[column.name]?.toString() || ""}
                                searchTerm={activeSearchTerm}
                              />
                            ) : column.name === "Date" ? (
                              <HighlightText
                                text={safeFormatDate(report[column.name])}
                                searchTerm={activeSearchTerm}
                              />
                            ) : column.name === "Plugging" ? (
                              <Typography
                                variant="body2"
                                sx={{ color: resolvePluggingColor(report.Rig) }}
                              >
                                {(() => {
                                  const a = rigAssessments[report.Rig]?.data;
                                  const raw = (
                                    a?.possiblePlugging ??
                                    a?.PossiblePlugging ??
                                    ""
                                  )
                                    .toString()
                                    .trim();
                                  return raw || "-";
                                })()}
                              </Typography>
                            ) : column.name === "Phase" ? (
                              <Typography variant="body2">
                                {(() => {
                                  const a = rigAssessments[report.Rig]?.data;
                                  const phase = (a?.phase ?? a?.Phase ?? "")
                                    .toString()
                                    .trim();
                                  return phase || "-";
                                })()}
                              </Typography>
                            ) : (
                              <HighlightText
                                text={report[column.name]?.toString() || ""}
                                searchTerm={activeSearchTerm}
                              />
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                      {selectedLocation !== "Oman" && (
                        <TableRow>
                          <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                            colSpan={visibleColumns.length + 1}
                          >
                            <Collapse
                              in={expandedRows.has(report.Id)}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ margin: 1 }}>
                                <Typography
                                  variant="h6"
                                  gutterBottom
                                  component="div"
                                >
                                  Details for {report.Rig} -{" "}
                                  {safeFormatDate(report.Date)}
                                </Typography>
                                {report.Details && (
                                  <DetailsTable
                                    searchTerm={activeSearchTerm}
                                    hideSearch={true}
                                    initialData={
                                      report.Details?.map((detail) => ({
                                        FromTo: detail.fromTo || "",
                                        Hrs: detail.hrs || 0,
                                        Lateral: detail.lateral || 0,
                                        Phase: detail.phase || "",
                                        Cat: detail.cat || "",
                                        MajorOP: detail.majorOP || "",
                                        Action: detail.action || "",
                                        Object: detail.object || "",
                                        Responsible: detail.responsible || "",
                                        Summary: detail.summary || "",
                                        Content: detail.content || "",
                                        Rig: report.Rig || "",
                                        Date: report.Date || "",
                                        Well: report.Well || "",
                                        FileUrl: report.FileUrl || "",
                                      })) || []
                                    }
                                  />
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={sortedReports.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[100]}
            />
            {/* Delete controls below the table */}
            {counts.totalReports > 1 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
                <Tooltip
                  title={
                    selectedLocation
                      ? `Delete all ${counts.totalReports} reports for ${format(
                          selectedDate,
                          "yyyy-MM-dd"
                        )} in ${selectedLocation}`
                      : "Select a location to enable delete"
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => setConfirmOpen(true)}
                      disabled={!selectedLocation}
                    >
                      Delete day ({counts.totalReports})
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ReportsTable;
